import { Tabs } from "@base-ui-components/react/tabs";
import { BadgeCheck, CreditCard, Palette, UserRound } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { BillingPanel } from "@/components/appshell/billing/BillingPanel";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/overlays";
import { Input, Separator } from "@/components/ui/primitives";
import { ErrorState, LoadingState } from "@/components/feedback";
import { ThemeSwitcher } from "@/brand/ThemeSwitcher";
import { accounts, type Accounts } from "@/lib/accounts";
import { api, GraphMindApiError } from "@/lib/api";
import { notifyError } from "@/lib/errors";
import { cn } from "@/lib/utils";
import { useShell, type SettingsTab } from "@/stores/shell";
import { useWritingEngine, type WritingEngine } from "@/stores/writing-engine";

interface AccountProfile {
  user_id: string;
  name: string;
  username: string | null;
  email: string | null;
  email_verified: boolean;
}

/**
 * FastAPI's default 422 body for a pydantic validation failure is
 * `{"detail": [{"loc": [...], "msg": "...", ...}]}` — a LIST, not the plain
 * string `detail` most other endpoints send (and the one shape `toUserError`
 * knows how to show verbatim). The username validator
 * (`graphmind/domain/accounts.py::normalize_username`) raises a plain
 * `ValueError`, which pydantic v2 reports as `"Value error, <message>"`;
 * strip that prefix so the already-written-to-be-clear backend text reads
 * naturally inline. Returns null for any shape this doesn't recognize, or
 * when no entry's `loc` mentions `field`, so the caller can fall back to the
 * shared `notifyError` copy instead of guessing. */
function fieldValidationMessage(err: GraphMindApiError, field: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(err.detail);
  } catch {
    return null;
  }
  const list = (parsed as { detail?: unknown } | null)?.detail;
  if (!Array.isArray(list)) return null;
  for (const item of list) {
    const loc = (item as { loc?: unknown })?.loc;
    const msg = (item as { msg?: unknown })?.msg;
    if (Array.isArray(loc) && loc.includes(field) && typeof msg === "string") {
      return msg.replace(/^Value error,\s*/, "");
    }
  }
  return null;
}

const TABS: { id: SettingsTab; label: string; icon: typeof UserRound }[] = [
  { id: "profile", label: "Profile", icon: UserRound },
  { id: "appearance", label: "Appearance", icon: Palette },
  { id: "billing", label: "Billing", icon: CreditCard },
];

function SettingsTabTrigger({ id, label, icon: Icon }: { id: SettingsTab; label: string; icon: typeof UserRound }) {
  return (
    <Tabs.Tab
      value={id}
      className={cn(
        "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium text-muted-foreground outline-none transition-colors",
        "hover:text-foreground data-[active]:bg-accent data-[active]:text-foreground",
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {label}
    </Tabs.Tab>
  );
}

function ProfilePanel() {
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [accs, setAccs] = useState<Accounts | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);

  useEffect(() => {
    void accounts().then(setAccs);
  }, []);

  const load = useCallback(() => {
    setLoadError(null);
    return api
      .request<AccountProfile>("GET", "/api/account/profile")
      .then((p) => {
        setProfile(p);
        setName(p.name);
        setUsername(p.username ?? "");
      })
      .catch((e: unknown) => setLoadError(e));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loadError) return <ErrorState error={loadError} onRetry={() => void load()} label="profile" />;
  if (!profile) return <LoadingState variant="skeleton" rows={4} label="Loading profile…" />;

  const dirty = name.trim() !== profile.name || username.trim().toLowerCase() !== (profile.username ?? "");

  const save = () => {
    setUsernameError(null);
    const body: { name?: string; username?: string } = {};
    if (name.trim() && name.trim() !== profile.name) body.name = name.trim();
    const trimmedUsername = username.trim().toLowerCase();
    if (trimmedUsername !== (profile.username ?? "")) {
      if (trimmedUsername.length === 0) {
        setUsernameError("A username can't be cleared once set.");
        return;
      }
      body.username = trimmedUsername;
    }
    if (Object.keys(body).length === 0) return;
    setBusy(true);
    api
      .request<AccountProfile>("PATCH", "/api/account/profile", body)
      .then((p) => {
        setProfile(p);
        setName(p.name);
        setUsername(p.username ?? "");
        toast.success("Profile updated");
      })
      .catch((e: unknown) => {
        if (e instanceof GraphMindApiError && e.status === 409) {
          setUsernameError("That username is taken.");
          return;
        }
        if (e instanceof GraphMindApiError && e.status === 422) {
          const detail = fieldValidationMessage(e, "username");
          if (detail) {
            setUsernameError(detail);
            return;
          }
        }
        notifyError(e);
      })
      .finally(() => setBusy(false));
  };

  const changeEmail = () => {
    if (!newEmail.trim() || emailBusy || !accs?.updateEmail) return;
    setEmailBusy(true);
    void accs
      .updateEmail(newEmail.trim())
      .then(() => {
        toast.success("Check your new address for a confirmation link");
        setNewEmail("");
      })
      .catch((e: unknown) => notifyError(e))
      .finally(() => setEmailBusy(false));
  };

  const changePassword = () => {
    if (newPassword.length < 6 || passwordBusy || !accs?.updatePassword) return;
    setPasswordBusy(true);
    void accs
      .updatePassword(newPassword)
      .then(() => {
        toast.success("Password updated");
        setNewPassword("");
      })
      .catch((e: unknown) => notifyError(e))
      .finally(() => setPasswordBusy(false));
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground">Email</p>
          <p className="truncate text-sm text-foreground">{profile.email ?? "—"}</p>
        </div>
        {profile.email && (
          <span
            className={cn(
              "inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]",
              profile.email_verified
                ? "border-primary/40 text-primary"
                : "border-border text-muted-foreground",
            )}
          >
            {profile.email_verified && <BadgeCheck className="h-3 w-3" />}
            {profile.email_verified ? "verified" : "unverified"}
          </span>
        )}
      </div>

      <label htmlFor="profile-name" className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-muted-foreground">Display name</span>
        <Input
          id="profile-name"
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
        />
      </label>

      <label htmlFor="profile-username" className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-muted-foreground">Username</span>
        <Input
          id="profile-username"
          name="username"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            setUsernameError(null);
          }}
          placeholder="lowercase-handle"
          maxLength={30}
        />
        {usernameError ? (
          <p className="text-xs text-destructive">{usernameError}</p>
        ) : (
          <p className="text-xs text-muted-foreground">
            3–30 characters: lowercase letters, digits, "_" or "-".
          </p>
        )}
      </label>

      <Button type="button" size="sm" className="w-fit" disabled={busy || !dirty} onClick={save}>
        Save changes
      </Button>

      {(accs?.updateEmail || accs?.updatePassword) && <Separator />}

      {accs?.updateEmail && (
        <label htmlFor="profile-new-email" className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Change email</span>
          <div className="flex gap-2">
            <Input
              id="profile-new-email"
              name="new-email"
              type="email"
              autoComplete="email"
              placeholder="new@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
            <Button type="button" size="sm" variant="outline" disabled={emailBusy || !newEmail.trim()} onClick={changeEmail}>
              Update
            </Button>
          </div>
        </label>
      )}

      {accs?.updatePassword && (
        <label htmlFor="profile-new-password" className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Change password</span>
          <div className="flex gap-2">
            <Input
              id="profile-new-password"
              name="new-password"
              type="password"
              placeholder="New password"
              autoComplete="new-password"
              value={newPassword}
              minLength={6}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={passwordBusy || newPassword.length < 6}
              onClick={changePassword}
            >
              Update
            </Button>
          </div>
        </label>
      )}
    </div>
  );
}

function AppearancePanel() {
  const engine = useWritingEngine((s) => s.engine);
  const setEngine = useWritingEngine((s) => s.setEngine);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 p-3">
        <div>
          <p className="text-sm text-foreground">Brand theme</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Recolors the whole studio — six palettes, dark-field.
          </p>
        </div>
        <ThemeSwitcher />
      </div>
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 p-3">
        <div>
          <p className="text-sm text-foreground">Writing engine</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Classic is the default — the pen, shapes and fill live there. Excalidraw
            is available if you prefer its scene editor. This only picks the engine for
            NEW notes and untouched PDFs; existing writing always reopens in the engine
            that made it.
          </p>
        </div>
        <div
          className="inline-flex shrink-0 rounded-md border border-border bg-muted/40 p-0.5"
          role="radiogroup"
          aria-label="Writing engine"
        >
          {(
            [
              { value: "legacy", label: "Classic" },
              { value: "excalidraw", label: "Excalidraw" },
            ] as { value: WritingEngine; label: string }[]
          ).map(({ value, label }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={engine === value}
              onClick={() => setEngine(value)}
              className={
                engine === value
                  ? "rounded bg-primary/20 px-2.5 py-1 text-[11px] text-primary"
                  : "rounded px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * One unified Settings surface (Profile / Appearance / Billing), replacing
 * the old three disjoint dialogs (`AccountSettingsDialog`, the `ThemeSwitcher`
 * dropdown alone, `BillingButton`'s dialog). `AccountChip` still owns identity
 * + sign-out; this owns everything else account-related.
 */
export function SettingsOverlay() {
  const open = useShell((s) => s.settingsOpen);
  const tab = useShell((s) => s.settingsTab);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => (v ? useShell.getState().openSettings() : useShell.getState().closeSettings())}
    >
      <DialogContent title="Settings" className="w-[min(640px,94vw)]">
        <Tabs.Root
          value={tab}
          onValueChange={(v) => useShell.getState().openSettings(v as SettingsTab)}
          className="flex flex-col gap-4"
        >
          <Tabs.List className="flex items-center gap-1 border-b border-border pb-3">
            {TABS.map((t) => (
              <SettingsTabTrigger key={t.id} id={t.id} label={t.label} icon={t.icon} />
            ))}
          </Tabs.List>
          <Tabs.Panel value="profile" className="outline-none">
            <ProfilePanel />
          </Tabs.Panel>
          <Tabs.Panel value="appearance" className="outline-none">
            <AppearancePanel />
          </Tabs.Panel>
          <Tabs.Panel value="billing" className="outline-none">
            <BillingPanel />
          </Tabs.Panel>
        </Tabs.Root>
      </DialogContent>
    </Dialog>
  );
}
